import { z } from 'zod';
import type { ProvisionStep } from './provisioning.js';

/**
 * Giving this platform an Asterisk Manager login, from here.
 *
 * The VOIP plugin needs three things on a PBX before it can see a single
 * channel: an AMI user it knows the secret for, a manager service actually
 * listening on an address we can reach, and an ACL that lets our address in.
 * On a fleet of FreePBX boxes that is three files and a reload each, done by
 * hand — which is why several PBXes sat at "AMI login failed" with nobody
 * quite sure which of the three was missing.
 *
 * This drives all three over the SSH session the platform already has, in the
 * same preview-then-apply shape as the MikroTik provisioning next door, and
 * with the same additive bias:
 *
 *  - our manager user lives in its own drop-in file we own outright, so a
 *    re-run overwrites our stanza and can never mangle an operator's other
 *    manager users;
 *  - the include line is appended only when it isn't already there;
 *  - `manager.conf` is backed up before its `[general]` block is touched, and
 *    only the three keys this feature is about (`enabled`, `bindaddr`, `port`)
 *    are rewritten — every other setting in the block is carried through.
 *
 * `[general]` is the one place this does edit rather than add, because it has
 * to: Asterisk reads the *first* value it finds for a key in a category, so an
 * appended `[general](+)` override would lose to whatever `manager.conf`
 * already says and the PBX would stay bound to 127.0.0.1.
 */

/** Drop-in file holding our manager user, and nothing else. */
export const AMI_DROPIN_FILE = 'manager_itr.conf';

/** Default Asterisk config directory. */
export const ASTERISK_CONF_DIR = '/etc/asterisk';

/**
 * Stand-in for the real secret in every built command.
 *
 * The step list is previewed in the browser, returned to the client and
 * written to the audit log, none of which should ever carry an AMI password.
 * The server swaps this token for the real secret in the instant before each
 * command is written to the SSH channel, and leaves the token (rendered as
 * bullets) in the copy it returns — so what the operator reviews and what runs
 * are built from one source, with the secret never in the transcript.
 */
export const AMI_SECRET_PLACEHOLDER = '__ITR_AMI_SECRET__';

/**
 * Manager permissions our user gets.
 *
 * Wide on read because the collectors are read-heavy (`CoreStatus`,
 * `CoreShowChannels`, `PJSIPShowEndpoints`, CDR events), narrow-ish on write:
 * `command` for the CLI fallbacks the VOIP plugin uses when AMI actions are
 * missing, `call` for the one control action the platform exposes (hang up a
 * live call), `reporting` and `system` for the status actions. No `originate`
 * — nothing here places calls, and an AMI login that can is a login that can
 * bill somebody's trunk.
 */
export const AMI_READ_PERMISSIONS = 'system,call,agent,user,config,command,dtmf,reporting,cdr,dialplan';
export const AMI_WRITE_PERMISSIONS = 'system,call,agent,config,command,reporting';

/* ------------------------------------------------------------------ */
/* Input safety                                                        */
/* ------------------------------------------------------------------ */

/**
 * Everything below is interpolated into a shell script and into an Asterisk
 * config file, so each value is whitelisted rather than escaped — two
 * grammars, neither of them forgiving, and "escaped correctly for both" is a
 * bet worth not taking. The flip side is that these patterns are also what
 * lets the generated script use an unquoted heredoc without risking expansion.
 */
const AMI_USERNAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/;
const AMI_SECRET_RE = /^[A-Za-z0-9_-]{8,64}$/;
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV4_CIDR_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}\/([0-9]|[12]\d|3[0-2])$/;

export function isValidAmiUsername(value: string): boolean {
  return AMI_USERNAME_RE.test(value);
}

/**
 * Letters, digits, `_` and `-` only, at least 8 long.
 *
 * Asterisk would accept far more, but a secret that cannot contain a quote,
 * a `$`, a backtick or a `;` is one that cannot break out of the config line
 * or the script that writes it, whoever types it.
 */
export function isValidAmiSecret(value: string): boolean {
  return AMI_SECRET_RE.test(value);
}

/** Bare IPv4, or IPv4/prefix — what an AMI `permit` and a firewall rule take. */
export function isValidAmiPermit(value: string): boolean {
  return IPV4_RE.test(value) || IPV4_CIDR_RE.test(value);
}

export function isValidBindAddress(value: string): boolean {
  return IPV4_RE.test(value);
}

/** `permit=` wants a prefix; a bare host address means just that host. */
export function toPermitCidr(value: string): string {
  return value.includes('/') ? value : `${value}/32`;
}

/* ------------------------------------------------------------------ */
/* Requests and results                                               */
/* ------------------------------------------------------------------ */

export const amiProvisionRequestSchema = z.object({
  /** SSH login. Omitted along with `vaultEntryId` means "use what the resource already has saved". */
  username: z.string().min(1).optional(),
  password: z.string().optional(),
  /** Pull the SSH login from this vault entry instead. */
  vaultEntryId: z.string().optional(),

  /** Manager user to create. Defaults to the resource's `amiUsername`, else `itr-monitor`. */
  amiUsername: z.string().max(32).optional(),
  /** Blank generates one — the usual path, since nobody needs to know it by hand. */
  amiSecret: z.string().max(64).optional(),
  amiPort: z.number().int().min(1).max(65535).optional(),
  /**
   * Which of the PBX's addresses to reach AMI on. Defaults to the resource's
   * saved `amiHost`, else its primary address. SSH goes to the same address,
   * so the source the PBX reports is the one AMI traffic will arrive from — a
   * box on two networks sees this server under a different address on each.
   */
  amiHost: z.string().max(253).optional(),
  /** What `bindaddr` becomes. 0.0.0.0 is the point of the exercise; overridable for a PBX that should only listen on one interface. */
  bindAddress: z.string().optional(),

  /**
   * Address to allow in, as the PBX sees us. Left blank, the server reads
   * `$SSH_CLIENT` on the box itself rather than the local end of its own
   * socket — with Docker masquerading the container and a VPN gateway NATing
   * again beyond it, those are different addresses, and an ACL written for the
   * socket's would match nothing.
   */
  sourceAddress: z.string().optional(),
  /** Further addresses or CIDRs to permit — a second dashboard node, say. */
  extraPermits: z.array(z.string().max(32)).max(8).default([]),

  /** Also open TCP `amiPort` in the host firewall (firewalld, else iptables). */
  openFirewall: z.boolean().default(true),
  /** Save the manager login onto the resource so the VOIP collector starts using it. */
  saveCredentials: z.boolean().default(true),
  /** false = show the exact commands without touching the PBX. */
  apply: z.boolean().default(false),
});
export type AmiProvisionRequest = z.infer<typeof amiProvisionRequestSchema>;

/** What the box told us about itself before anything was written. */
export interface AsteriskFacts {
  hasAsterisk: boolean;
  hasManagerConf: boolean;
  freepbx: boolean;
  /** `core show version` output, when the CLI answered. */
  version: string | null;
  /** `manager.conf`'s `[general]` block as found — what the edit is changing from. */
  existingGeneral: string | null;
  /** Whether the Asterisk CLI answered at all; if not, the reload step can't run. */
  cliReachable: boolean;
  /**
   * Other manager config files that already define this username.
   *
   * Asterisk keeps the *first* definition of a category it loads, so a user
   * already declared in `manager.conf` or someone's own `manager_custom.conf`
   * would win over our drop-in and quietly ignore the secret we just wrote —
   * the kind of thing that reads as "the password is wrong" forever.
   */
  userDefinedIn: string[];
}

export interface AmiProvisionResult {
  applied: boolean;
  facts: AsteriskFacts;
  /** Address the PBX sees this server on — what went into the ACL. */
  sourceAddress: string | null;
  permits: string[];
  amiUsername: string;
  amiPort: number;
  /** Address the collector connects to AMI on. */
  amiHost: string;
  bindAddress: string;
  /** File the `#include` line goes in — `manager_custom.conf` on FreePBX. */
  includeFile: string;
  steps: Array<ProvisionStep & { output?: string; ok?: boolean; error?: string }>;
  /** Did an AMI login from this server actually work afterwards? */
  verified: boolean | null;
  verifyMessage: string | null;
  /** Were the credentials written onto the resource for the collector to use? */
  credentialsSaved: boolean;
  /** The generated secret, returned once so it can be recorded elsewhere. */
  generatedSecret: string | null;
  /** Things the operator should know that aren't failures. */
  notes: string[];
}

/* ------------------------------------------------------------------ */
/* Read-only probes                                                    */
/* ------------------------------------------------------------------ */

/**
 * The address the PBX sees our session arrive from, straight out of the
 * session's own environment. Runs as the login user, not through sudo, because
 * sudo's `env_reset` is exactly what would strip `SSH_CLIENT`.
 */
export const ASTERISK_SOURCE_COMMAND = [
  'printf "ssh_client=%s\\n" "${SSH_CLIENT:-}"',
  'printf "ssh_connection=%s\\n" "${SSH_CONNECTION:-}"',
  'printf "login_user=%s\\n" "$(id -un)"',
].join('\n');

/** `SSH_CLIENT` is "<client ip> <client port> <server port>". */
export function parseSshClientAddress(output: string): string | null {
  for (const key of ['ssh_client', 'ssh_connection']) {
    const line = new RegExp(`^${key}=(.*)$`, 'm').exec(output)?.[1]?.trim();
    const first = line?.split(/\s+/)[0];
    if (first && IPV4_RE.test(first)) return first;
  }
  return null;
}

/**
 * Is this an Asterisk at all, is FreePBX on top of it, and what does the
 * manager's `[general]` block say right now.
 *
 * Needs root on most builds: `manager.conf` is 640 asterisk:asterisk and the
 * CLI control socket is 660, so a plain login can read neither.
 */
export function asteriskFactsCommand(amiUsername: string, confDir = ASTERISK_CONF_DIR): string {
  return [
    `conf=${confDir}`,
    'if command -v asterisk >/dev/null 2>&1; then echo has-asterisk; fi',
    'if command -v fwconsole >/dev/null 2>&1; then echo has-fwconsole; fi',
    'if [ -f /etc/freepbx.conf ]; then echo has-freepbx; fi',
    'if [ -f "$conf/manager.conf" ]; then echo has-manager-conf; fi',
    'if [ -f "$conf/manager_custom.conf" ]; then echo has-manager-custom; fi',
    'printf "version=%s\\n" "$(asterisk -rx "core show version" 2>/dev/null | head -n 1)"',
    `echo "${FACTS_USER_MARKER}"`,
    `grep -l "^[[:space:]]*\\[${amiUsername}\\][[:space:]]*$" "$conf"/manager*.conf 2>/dev/null | grep -v "/${AMI_DROPIN_FILE}$" || true`,
    `echo "${FACTS_GENERAL_MARKER}"`,
    `awk '/^[[:space:]]*\\[/ { ing = ($0 ~ /^[[:space:]]*\\[general\\]/) } ing { print }' "$conf/manager.conf" 2>/dev/null | head -n 40`,
  ].join('\n');
}

/** Output section markers, so one round trip answers several questions. */
const FACTS_USER_MARKER = '--- user-defined-in ---';
const FACTS_GENERAL_MARKER = '--- general ---';

function section(output: string, from: string, to?: string): string {
  const after = output.split(from)[1];
  if (after === undefined) return '';
  return (to ? after.split(to)[0]! : after).trim();
}

export function parseAsteriskFacts(output: string): AsteriskFacts {
  const version = /^version=(.*)$/m.exec(output)?.[1]?.trim() ?? '';
  const general = section(output, FACTS_GENERAL_MARKER);
  return {
    hasAsterisk: /^has-asterisk$/m.test(output),
    hasManagerConf: /^has-manager-conf$/m.test(output),
    freepbx: /^has-freepbx$/m.test(output) || /^has-fwconsole$/m.test(output),
    version: version || null,
    existingGeneral: general || null,
    cliReachable: /asterisk/i.test(version),
    userDefinedIn: section(output, FACTS_USER_MARKER, FACTS_GENERAL_MARKER)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.startsWith('/')),
  };
}

/* ------------------------------------------------------------------ */
/* The steps                                                           */
/* ------------------------------------------------------------------ */

export interface AmiStepOptions {
  amiUsername: string;
  amiPort: number;
  bindAddress: string;
  /** Already normalised to CIDR form, localhost first. */
  permits: string[];
  freepbx: boolean;
  openFirewall: boolean;
  /** Suffix for the `manager.conf` backup, so re-runs don't overwrite the original. */
  backupSuffix: string;
  confDir?: string;
}

/**
 * Where the `#include` goes.
 *
 * On plain Asterisk, `manager.conf` — we own the edit and nothing else
 * rewrites the file. On FreePBX it goes in `manager_custom.conf` instead:
 * FreePBX's own tooling regenerates `manager.conf` (its template always
 * re-adds the `_custom` includes) but never touches `_custom` files, so an
 * include placed there survives a framework upgrade that would otherwise
 * silently drop our manager user.
 */
export function amiIncludeFile(freepbx: boolean): string {
  return freepbx ? 'manager_custom.conf' : 'manager.conf';
}

/**
 * Every command this runs on the PBX, in order.
 *
 * Each entry is a complete `sh` script rather than a one-liner, so a step is
 * one reviewable unit of work and `set -e` makes a half-done step impossible.
 */
export function asteriskAmiSteps(options: AmiStepOptions): ProvisionStep[] {
  const conf = options.confDir ?? ASTERISK_CONF_DIR;
  const dropIn = `${conf}/${AMI_DROPIN_FILE}`;
  const includeFile = `${conf}/${amiIncludeFile(options.freepbx)}`;
  const permitLines = options.permits.map((p) => `permit = ${p}`).join('\n');

  const steps: ProvisionStep[] = [
    {
      title: 'Back up manager.conf before anything is changed',
      root: true,
      readOnly: false,
      command: [
        'set -e',
        `f=${conf}/manager.conf`,
        'if [ ! -f "$f" ]; then echo "no manager.conf at $f — is Asterisk installed?" >&2; exit 1; fi',
        `b="$f.itr-${options.backupSuffix}.bak"`,
        'if [ ! -f "$b" ]; then cp -a "$f" "$b"; fi',
        'ls -l "$b"',
      ].join('\n'),
    },
    {
      title: `Write the "${options.amiUsername}" manager user, restricted to this server`,
      root: true,
      readOnly: false,
      // Unquoted heredoc delimiter on purpose: every interpolated value is
      // whitelisted to characters the shell does not expand, and leaving it
      // unquoted keeps the previewed script readable.
      command: [
        'set -e',
        `f=${dropIn}`,
        'umask 027',
        'cat > "$f" <<ITR_EOF',
        ';',
        '; Asterisk Manager user for the IT Resources dashboard.',
        '; Written automatically — this file is owned by the dashboard and is',
        '; replaced in full on every run. Put your own manager users elsewhere.',
        ';',
        `[${options.amiUsername}]`,
        `secret = ${AMI_SECRET_PLACEHOLDER}`,
        'deny = 0.0.0.0/0.0.0.0',
        permitLines,
        `read = ${AMI_READ_PERMISSIONS}`,
        `write = ${AMI_WRITE_PERMISSIONS}`,
        'ITR_EOF',
        `chown --reference="${conf}/manager.conf" "$f" 2>/dev/null || true`,
        'chmod 640 "$f"',
        'ls -l "$f"',
      ].join('\n'),
    },
    {
      title: `Make the manager listen on ${options.bindAddress}:${options.amiPort}`,
      root: true,
      readOnly: false,
      // The only edit of existing config here. Rewrites exactly `enabled`,
      // `bindaddr` and `port` inside `[general]` — including commented-out
      // ones, which is the usual reason a PBX is still on 127.0.0.1 — and
      // passes every other line through untouched. `cat >` rather than `mv`
      // so the file keeps its own inode, owner and mode.
      command: [
        'set -e',
        `f=${conf}/manager.conf`,
        'if ! grep -qi "^[[:space:]]*\\[general\\]" "$f"; then printf "[general]\\n" >> "$f"; fi',
        't="$f.itr-new"',
        `awk -v p=${options.amiPort} -v b=${options.bindAddress} '`,
        '  /^[[:space:]]*\\[/ {',
        '    ing = ($0 ~ /^[[:space:]]*\\[general\\]/)',
        '    print',
        '    if (ing && !seen) { seen = 1; print "enabled = yes"; print "bindaddr = " b; print "port = " p }',
        '    next',
        '  }',
        '  ing && /^[[:space:]]*;*[[:space:]]*(enabled|bindaddr|port)[[:space:]]*=/ { next }',
        '  { print }',
        `' "$f" > "$t"`,
        'cat "$t" > "$f"',
        'rm -f "$t"',
        `awk '/^[[:space:]]*\\[/ { ing = ($0 ~ /^[[:space:]]*\\[general\\]/) } ing { print }' "$f" | head -n 12`,
      ].join('\n'),
    },
    {
      // After the `[general]` edit, so that on the one pathological layout
      // where `manager.conf` has no `[general]` at all the appended section
      // lands above the include rather than below it.
      title: `Include it from ${amiIncludeFile(options.freepbx)} (added only if not already there)`,
      root: true,
      readOnly: false,
      command: [
        'set -e',
        `f=${includeFile}`,
        'if [ ! -f "$f" ]; then',
        '  umask 027',
        '  : > "$f"',
        `  chown --reference="${conf}/manager.conf" "$f" 2>/dev/null || true`,
        'fi',
        `if grep -q "^[[:space:]]*#include[[:space:]]*${AMI_DROPIN_FILE}" "$f"; then`,
        '  echo "#include already present in $f"',
        'else',
        `  printf "\\n#include ${AMI_DROPIN_FILE}\\n" >> "$f"`,
        '  echo "added #include to $f"',
        'fi',
      ].join('\n'),
    },
  ];

  // Only the addresses that actually arrive over the network; a firewall rule
  // for 127.0.0.1 would be noise, and an empty list means there is nothing to
  // open at all rather than a `for` loop over nothing.
  const routablePermits = options.permits.filter((p) => !p.startsWith('127.'));

  if (options.openFirewall && routablePermits.length > 0) {
    steps.push({
      title: `Allow TCP ${options.amiPort} from this server in the host firewall`,
      root: true,
      readOnly: false,
      // firewalld first because its rules persist; the iptables branch checks
      // for its own rule before inserting, so a re-run doesn't stack copies.
      command: [
        'set -e',
        `for src in ${routablePermits.join(' ')}; do`,
        '  if command -v firewall-cmd >/dev/null 2>&1 && firewall-cmd --state >/dev/null 2>&1; then',
        `    firewall-cmd --permanent --add-rich-rule="rule family=ipv4 source address=$src port port=${options.amiPort} protocol=tcp accept" || true`,
        '    changed=1',
        '  elif command -v iptables >/dev/null 2>&1; then',
        `    if iptables -C INPUT -p tcp -s "$src" --dport ${options.amiPort} -j ACCEPT 2>/dev/null; then`,
        '      echo "iptables rule for $src already present"',
        '    else',
        `      iptables -I INPUT 1 -p tcp -s "$src" --dport ${options.amiPort} -j ACCEPT`,
        '      echo "inserted iptables rule for $src"',
        '    fi',
        '  else',
        '    echo "neither firewalld nor iptables found — nothing to open for $src"',
        '  fi',
        'done',
        'if [ -n "${changed:-}" ]; then firewall-cmd --reload; fi',
      ].join('\n'),
    });
  }

  steps.push({
    title: 'Reload the manager and read back its settings',
    root: true,
    readOnly: false,
    // Deliberately soft: a PBX with Asterisk stopped still gets valid config
    // written above, and the AMI login check below is what decides whether
    // this actually worked.
    command: [
      'asterisk -rx "manager reload" 2>&1 || echo "could not reach the Asterisk CLI — reload it yourself once Asterisk is running"',
      'asterisk -rx "manager show settings" 2>&1 | head -n 12 || true',
    ].join('\n'),
  });

  return steps;
}
